import { useEffect, useState } from "react";

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

export default function Dialog({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  footer, 
  className,
  size = 'md'
}: DialogProps) {
  const [mounted, setMounted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl'
  };

  useEffect(() => {
    setMounted(true);
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      setIsFullscreen(false);
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!mounted || !isOpen) return null;

  return (
    <div className={`fixed inset-0 z-[100] flex items-center justify-center animate-in fade-in duration-300 ${isFullscreen ? 'p-0' : 'p-4'}`}>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-on-surface/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog Container */}
      <div className={`relative bg-surface-container-lowest shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-8 duration-500 ${className || 'border border-outline-variant/20'} ${isFullscreen ? 'w-screen h-screen max-w-none max-h-none rounded-none' : `w-full ${sizeClasses[size]} rounded-[1.75rem] sm:rounded-[2.5rem] max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)]`}`}>
        
        {/* Sticky Header */}
        <div className="flex justify-between items-center p-5 sm:p-8 pb-3 sm:pb-4 bg-surface-container-lowest/80 backdrop-blur-xl z-10 border-b border-outline-variant/5">
          <h3 className="text-xl sm:text-2xl font-black text-on-surface tracking-tighter">{title}</h3>
          <div className="flex items-center gap-2">
            <button 
              type="button"
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-surface-container-high transition-colors"
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              <span className="material-symbols-outlined text-on-surface-variant">
                {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
              </span>
            </button>
            <button 
              type="button"
              onClick={onClose}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-surface-container-high transition-colors"
            >
              <span className="material-symbols-outlined text-on-surface-variant">close</span>
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-8 pt-4 sm:pt-6 custom-scrollbar">
          <div className="space-y-6">
            {children}
          </div>
        </div>

        {/* Sticky Footer */}
        {footer && (
          <div className="p-5 sm:p-8 pt-3 sm:pt-4 bg-surface-container-lowest border-t border-outline-variant/5">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
